import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Edit2, X, Check, Calendar, ArrowUpCircle, ArrowDownCircle, Filter, FileDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useProject } from '../context/ProjectContext';
import { useConfirm } from '../context/ConfirmContext';
import { useDataVersion } from '../context/DataVersionContext';
import { formatBRL, formatDateBR, todayBR } from '../lib/format';
import { exportPayableReceivablePDF } from '../lib/exportPdf';
import DateInput from '../components/DateInput';
import BotaoFechar, { useEscFecha } from '../components/BotaoFechar';
import MoneyInput from '../components/MoneyInput';
import TagInput from '../components/TagInput';
import SummaryCards from '../components/SummaryCards';
import { colorOptionStyle } from '../lib/colorTint';

const EMPTY_PR_FILTERS = {
  search: '',
  type: '',         // payable | receivable
  status: '',       // pending | settled (paid/received) | overdue
  start: '',        // due_date >=
  end: '',          // due_date <=
  recurring: '',    // '' | 'yes' | 'no'
};

// --- Quick presets ---------------------------------------------------------
// Each preset knows how to build { start, end, status } from today's ISO date.
function _addDays(iso, n) {
  const [Y, M, D] = iso.split('-').map(Number);
  const d = new Date(Y, M - 1, D);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PR_PRESETS = [
  { id: 'all', label: 'Todos', build: () => ({ start: '', end: '', status: '' }) },
  { id: 'overdue', label: 'Vencidos', build: (t) => ({ start: '', end: _addDays(t, -1), status: 'overdue' }) },
  { id: 'this-month', label: 'Este mês', build: (t) => {
    const [Y, M] = t.split('-').map(Number);
    const last = new Date(Y, M, 0).getDate();
    const mm = String(M).padStart(2, '0');
    return { start: `${Y}-${mm}-01`, end: `${Y}-${mm}-${String(last).padStart(2, '0')}`, status: '' };
  }},
  { id: 'next-7', label: 'Próximos 7 dias', build: (t) => ({ start: t, end: _addDays(t, 7), status: 'pending' }) },
  { id: 'next-30', label: 'Próximos 30 dias', build: (t) => ({ start: t, end: _addDays(t, 30), status: 'pending' }) },
  { id: 'next-90', label: 'Próximos 90 dias', build: (t) => ({ start: t, end: _addDays(t, 90), status: 'pending' }) },
];

function isActivePreset(filters, preset) {
  const built = preset.build(todayBR());
  return filters.start === built.start
    && filters.end === built.end
    && filters.status === built.status;
}

function countActivePRFilters(f) {
  let n = 0;
  if (f.type) n++;
  if (f.status) n++;
  if (f.start) n++;
  if (f.end) n++;
  if (f.recurring) n++;
  return n;
}

export default function PayableReceivable() {
  const { currentProject } = useProject();
  const confirm = useConfirm();
  const { bump, version } = useDataVersion();
  const [items, setItems] = useState([]);
  const [pots, setPots] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tags, setTags] = useState([]);
  const [filters, setFilters] = useState(EMPTY_PR_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const activeFilters = countActivePRFilters(filters);
  const clearFilters = () => setFilters({ ...EMPTY_PR_FILTERS, search: filters.search });

  const load = useCallback(async () => {
    if (!currentProject) return;
    setLoading(true);
    try {
      const pid = currentProject.id;
      const [r, p, a, t] = await Promise.all([
        api.get(`/projects/${pid}/payable_receivable`),
        api.get(`/projects/${pid}/pots`),
        api.get(`/projects/${pid}/accounts`),
        api.get(`/projects/${pid}/tags`),
      ]);
      setItems(r.data); setPots(p.data); setAccounts(a.data); setTags(t.data);
    } finally { setLoading(false); }
  }, [currentProject]);
  useEffect(() => { load(); }, [load, version]);

  const save = async (data) => {
    try {
      if (editing) await api.put(`/payable_receivable/${editing.id}`, data);
      else await api.post(`/projects/${currentProject.id}/payable_receivable`, data);
      toast.success(editing ? 'Atualizado' : 'Criado');
      setShowForm(false); setEditing(null); await load(); bump();
    } catch (err) {
      toast.error('Erro ao salvar', { description: err?.response?.data?.detail || err.message });
    }
  };
  const remove = async (item) => {
    const ok = await confirm({
      title: `Excluir esta ${item.type === 'payable' ? 'conta a pagar' : 'conta a receber'}?`,
      description: `${item.description || '(sem descrição)'} · ${formatBRL(item.amount)}`,
      confirmText: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/payable_receivable/${item.id}`);
      toast.success('Excluído');
      await load(); bump();
    } catch (err) {
      toast.error('Erro ao excluir', { description: err?.response?.data?.detail || err.message });
    }
  };
  const togglePaid = async (it) => {
    try {
      if (it.status === 'pending') {
        await api.post(`/payable_receivable/${it.id}/settle`, { amount: it.amount });
        toast.success(it.type === 'payable' ? 'Marcado como pago' : 'Marcado como recebido', {
          description: 'Transação criada automaticamente.',
        });
      } else {
        await api.post(`/payable_receivable/${it.id}/unsettle`);
        toast.success('Reaberto', { description: 'Transação revertida.' });
      }
      await load(); bump();
    } catch (err) {
      toast.error('Erro ao atualizar', { description: err?.response?.data?.detail || err.message });
    }
  };

  const filtered = useMemo(() => {
    const today = todayBR();
    const q = filters.search.trim().toLowerCase();
    return items.filter((i) => {
      if (filters.type && i.type !== filters.type) return false;
      if (filters.status === 'pending' && i.status !== 'pending') return false;
      if (filters.status === 'settled' && i.status === 'pending') return false;
      if (filters.status === 'overdue') {
        if (i.status !== 'pending') return false;
        if (!(i.due_date < today)) return false;
      }
      if (filters.start && i.due_date < filters.start) return false;
      if (filters.end && i.due_date > filters.end) return false;
      if (filters.recurring === 'yes' && !i.is_recurring) return false;
      if (filters.recurring === 'no' && i.is_recurring) return false;
      if (q) {
        const blob = `${i.description || ''} ${i.amount} ${formatDateBR(i.due_date)}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [items, filters]);

  const totals = useMemo(() => {
    let receivable = 0, payable = 0;
    for (const i of filtered) {
      if (i.status !== 'pending') continue;
      if (i.type === 'receivable') receivable += i.amount || 0;
      else if (i.type === 'payable') payable += i.amount || 0;
    }
    return { receivable, payable, balance: receivable - payable };
  }, [filtered]);

  if (!currentProject) return <div className="text-slate-500 dark:text-slate-400">Selecione um projeto.</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">A Pagar / Receber</h2>
          <p className="text-slate-500 dark:text-slate-400">Gerencie suas contas pendentes e quitadas</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg shadow-md self-start" data-testid="pr-new-btn">
          <Plus className="w-4 h-4" /> Novo
        </button>
      </div>

      <SummaryCards
        income={totals.receivable}
        expense={totals.payable}
        balance={totals.balance}
        suffix="Pendente (Filtrado)"
        hide={!activeFilters && !filters.search.trim()}
      />

      {/* Toolbar (global filter pattern) */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Buscar (descrição, valor, data...)"
            className="input-md pl-10 pr-10" data-testid="pr-search-input" />
          {filters.search && (
            <button type="button" onClick={() => setFilters({ ...filters, search: '' })} data-testid="pr-search-clear-icon"
              aria-label="Limpar busca" title="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button onClick={() => setShowFilters((v) => !v)} data-testid="pr-filters-toggle"
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${showFilters || activeFilters ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
          <Filter className="w-4 h-4" /> Filtros
          {activeFilters > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-indigo-600 text-white text-xs font-semibold px-1.5" data-testid="pr-filters-count">{activeFilters}</span>
          )}
        </button>
        {activeFilters > 0 && (
          <button onClick={clearFilters} data-testid="pr-filters-clear"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
            <X className="w-4 h-4" /> Limpar
          </button>
        )}
        <button onClick={() => exportPayableReceivablePDF(filtered, currentProject.name)}
          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700" data-testid="pr-export-btn">
          <FileDown className="w-4 h-4" /> PDF
        </button>
      </div>

      {showFilters && (
        <div className="card p-5 space-y-4" data-testid="pr-filters-panel">
          <div>
            <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">Atalhos rápidos</div>
            <div className="flex flex-wrap gap-2" data-testid="pr-quick-presets">
              {PR_PRESETS.map((p) => {
                const active = isActivePreset(filters, p);
                return (
                  <button key={p.id} type="button" onClick={() => setFilters({ ...filters, ...p.build(todayBR()) })}
                    data-testid={`pr-preset-${p.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Tipo</label>
              <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="input-md" data-testid="pr-filter-type">
                <option value="">Todos</option>
                <option value="payable">A pagar</option>
                <option value="receivable">A receber</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Status</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="input-md" data-testid="pr-filter-status">
                <option value="">Todos</option>
                <option value="pending">Pendentes</option>
                <option value="settled">Quitados</option>
                <option value="overdue">Vencidos</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Recorrente</label>
              <select value={filters.recurring} onChange={(e) => setFilters({ ...filters, recurring: e.target.value })} className="input-md" data-testid="pr-filter-recurring">
                <option value="">Todos</option>
                <option value="yes">Apenas recorrentes</option>
                <option value="no">Sem recorrência</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Vencimento inicial</label>
              <DateInput value={filters.start} onChange={(v) => setFilters({ ...filters, start: v || '' })} />
            </div>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Vencimento final</label>
              <DateInput value={filters.end} onChange={(v) => setFilters({ ...filters, end: v || '' })} />
            </div>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-400">
          {loading ? 'Carregando...' : `Mostrando ${filtered.length} de ${items.length} ${items.length === 1 ? 'item' : 'itens'}`}
        </div>
        {!loading && filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">Nenhum item.</div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {filtered.map((it) => {
              const isPaid = it.status !== 'pending';
              const overdue = !isPaid && it.due_date < todayBR();
              return (
                <div key={it.id} className="flex items-start sm:items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-slate-50 dark:hover:bg-slate-700/40" data-testid={`pr-row-${it.id}`}>
                  <button onClick={() => togglePaid(it)}
                    aria-label={isPaid ? 'Marcar como pendente' : 'Marcar como pago'}
                    className={`shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 ${isPaid ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 dark:border-slate-600 hover:border-emerald-500 text-transparent hover:text-emerald-500'}`}>
                    <Check className="w-5 h-5" />
                  </button>
                  <div className={`hidden sm:flex shrink-0 p-2 rounded-lg ${it.type === 'payable' ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>
                    {it.type === 'payable' ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 sm:hidden">
                      <div className={`font-medium text-sm break-words ${isPaid ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>{it.description || '(sem descrição)'}</div>
                      <div className={`font-semibold text-sm whitespace-nowrap ${it.type === 'payable' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{formatBRL(it.amount)}</div>
                    </div>
                    <div className={`hidden sm:block font-medium ${isPaid ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>{it.description || '(sem descrição)'}</div>
                    <div className={`text-[11px] sm:text-xs flex items-center gap-1 flex-wrap mt-0.5 ${overdue ? 'text-rose-600 dark:text-rose-400 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                      <Calendar className="w-3 h-3 shrink-0" />
                      <span>{formatDateBR(it.due_date)}</span>
                      {overdue && <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 font-semibold text-[10px]">VENCIDO</span>}
                      {it.is_recurring && <span className="text-slate-500 dark:text-slate-400">· Recorrente</span>}
                    </div>
                    <div className="flex sm:hidden gap-1 mt-1">
                      <button onClick={() => { setEditing(it); setShowForm(true); }} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => remove(it)} className="p-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className={`hidden sm:block font-semibold whitespace-nowrap ${it.type === 'payable' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{formatBRL(it.amount)}</div>
                  <div className="hidden sm:flex gap-1">
                    <button onClick={() => { setEditing(it); setShowForm(true); }} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(it)} className="p-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <PRForm initial={editing} pots={pots} accounts={accounts} tags={tags}
          onCreateTag={async (name) => {
            const { data } = await api.post(`/projects/${currentProject.id}/tags`, { name });
            setTags((prev) => [...prev, data]);
            return data;
          }}
          onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} />
      )}
    </div>
  );
}

function PRForm({ initial, pots, accounts, tags, onCreateTag, onClose, onSave }) {
  useEscFecha(onClose);
  const today = todayBR();
  const [form, setForm] = useState(initial ? {
    ...initial,
    pot_exit_id: initial.pot_exit_id ?? initial.pot_id ?? null,
    pot_entry_id: initial.pot_entry_id ?? null,
    account_exit_id: initial.account_exit_id ?? initial.account_id ?? null,
    account_entry_id: initial.account_entry_id ?? null,
    tag_ids: initial.tag_ids || (initial.tags || []).map((t) => t.id) || [],
    days_before_due: initial.days_before_due ?? 0,
  } : {
    type: 'payable', amount: 0, due_date: today, description: '', notes: '',
    status: 'pending',
    pot_exit_id: null, pot_entry_id: null, account_exit_id: null, account_entry_id: null,
    tag_ids: [],
    is_recurring: 1, recurrence_frequency: 'monthly', days_before_due: 0,
    variable_amount: false, installments_total: null,
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const intOrNull = (v) => v ? parseInt(v) : null;
  const isPayable = form.type === 'payable';

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      amount: parseFloat(form.amount) || 0,
      is_recurring: form.is_recurring ? 1 : 0,
      installments_total: form.installments_total ? parseInt(form.installments_total) : null,
      days_before_due: parseInt(form.days_before_due) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{initial ? 'Editar' : 'Nova'} Conta a Pagar/Receber</h3>
          <BotaoFechar onClose={onClose} testid="pr-form-fechar" />
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {/* Descrição */}
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Descrição</label>
            <input value={form.description || ''} onChange={(e) => upd('description', e.target.value)}
              className="input-md mt-1" placeholder="Ex: Fatura - Cartão de Crédito" />
          </div>

          {/* Data + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Data de Vencimento</label>
              <DateInput value={form.due_date} onChange={(v) => upd('due_date', v)} required />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Status</label>
              <select value={form.status || 'pending'} onChange={(e) => upd('status', e.target.value)} className="input-md mt-1">
                <option value="pending">Pendente</option>
                <option value={isPayable ? 'paid' : 'received'}>{isPayable ? 'Pago' : 'Recebido'}</option>
              </select>
            </div>
          </div>

          {/* Tipo + Valor */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Tipo</label>
              <select value={form.type} onChange={(e) => upd('type', e.target.value)} className="input-md mt-1">
                <option value="payable">A Pagar</option>
                <option value="receivable">A Receber</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Valor <span className="text-rose-500">*</span></label>
              <MoneyInput value={form.amount} onChange={(v) => upd('amount', v)} required placeholder="0,00" className="input-md mt-1" />
            </div>
          </div>

          {/* Observações + Tags */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Observações (opcional)</label>
              <input value={form.notes || ''} onChange={(e) => upd('notes', e.target.value)}
                placeholder="Observações adicionais" className="input-md mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Tags (opcional)</label>
              <TagInput tags={tags} selectedIds={form.tag_ids || []}
                onChange={(ids) => upd('tag_ids', ids)} onCreateTag={onCreateTag}
                placeholder="Digite para criar ou selecionar tag" />
            </div>
          </div>

          {/* Configurações de Entrada e Saída */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-3">
            <div>
              <h4 className="font-semibold text-slate-800 dark:text-slate-200">Configurações de Entrada e Saída</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Configure os potes e contas envolvidos. Use campos de saída para contas a pagar e entrada para contas a receber.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="Pote Saída" value={form.pot_exit_id} onChange={(v) => upd('pot_exit_id', intOrNull(v))} options={pots} emptyLabel="Nenhum" />
              <SelectField label="Pote Entrada" value={form.pot_entry_id} onChange={(v) => upd('pot_entry_id', intOrNull(v))} options={pots} emptyLabel="Nenhum" />
              <SelectField label="Conta Saída" value={form.account_exit_id} onChange={(v) => upd('account_exit_id', intOrNull(v))} options={accounts} emptyLabel="Nenhuma" />
              <SelectField label="Conta Entrada" value={form.account_entry_id} onChange={(v) => upd('account_entry_id', intOrNull(v))} options={accounts} emptyLabel="Nenhuma" />
            </div>
          </div>

          {/* Configurações de Recorrência */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-3">
            <h4 className="font-semibold text-slate-800 dark:text-slate-200">Configurações de Recorrência</h4>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
              <input type="checkbox" checked={!!form.is_recurring}
                onChange={(e) => upd('is_recurring', e.target.checked ? 1 : 0)}
                className="w-4 h-4 accent-indigo-600" data-testid="pr-recurring-toggle" />
              Conta Recorrente
            </label>
            {!!form.is_recurring && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Frequência de Recorrência</label>
                  <select value={form.recurrence_frequency || 'monthly'} onChange={(e) => upd('recurrence_frequency', e.target.value)}
                    className="input-md mt-1">
                    <option value="daily">Diário</option>
                    <option value="weekly">Semanal</option>
                    <option value="biweekly">Quinzenal</option>
                    <option value="monthly">Mensal</option>
                    <option value="bimonthly">Bimestral</option>
                    <option value="quarterly">Trimestral</option>
                    <option value="semiannual">Semestral</option>
                    <option value="yearly">Anual</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Criar quantos dias antes do vencimento</label>
                  <input type="number" min="0" value={form.days_before_due || 0}
                    onChange={(e) => upd('days_before_due', e.target.value)} className="input-md mt-1" />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">O sistema criará automaticamente a próxima recorrência X dias antes da data de vencimento</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={!!form.variable_amount}
                      onChange={(e) => upd('variable_amount', e.target.checked)}
                      className="w-4 h-4 mt-0.5 accent-indigo-600" data-testid="pr-variable-toggle" />
                    <span>
                      <span className="font-medium">Valor Variável</span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">O valor muda a cada recorrência (ex: fatura de cartão)</span>
                    </span>
                  </label>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Número de Parcelas (opcional)</label>
                    <input type="number" min="1" value={form.installments_total || ''}
                      onChange={(e) => upd('installments_total', e.target.value)} placeholder="Ex: 12" className="input-md mt-1" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">Cancelar</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg font-medium">{initial ? 'Atualizar' : 'Criar'} Conta</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, emptyLabel = '-' }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="input-md mt-1">
        <option value="">{emptyLabel}</option>
        {options.map((o) => <option key={o.id} value={o.id} style={colorOptionStyle(o.color)}>{o.name}</option>)}
      </select>
    </div>
  );
}
